import { numberToCurrency } from '@/utils'
import { isNotStarted } from '../model/accountMonths'
import { monthLabel } from '../model/months'
import type {
  AllAccountsAsOf,
  MonthTotal,
  SeriesPoint,
  StartingAccount,
} from '../model/portfolioOverview'
import type { AccountMonth } from '../model/types'
import {
  cashSourceNote,
  countOf,
  joinWithAnd,
  longDate,
  securitiesSourceNote,
} from '../portfolioText'

const MAX_NOTES = 3

// "statements start Nov 2024"
const startsText = (account: StartingAccount) =>
  account.startMonth
    ? `statements start ${monthLabel(account.startMonth)}`
    : 'no statements yet'

// Said only when no chosen account has started, so the month has nothing to add up.
const notStartedNotes = (total: MonthTotal) =>
  total.accountCount > 0
    ? []
    : total.notStarted.map(
        (account) => `${account.accountName}: ${startsText(account)}.`,
      )

const notesFor = (
  accountMonths: AccountMonth[],
  sentence: (entry: AccountMonth) => string,
) => accountMonths.map(sentence)

// Why the hero says "No total", one sentence per account that blocks it.
export const noTotalNotes = (total: MonthTotal): string[] => {
  const notes = [
    ...notesFor(
      total.missing,
      (entry) =>
        `${entry.accountName} has no ${monthLabel(entry.month)} statement.`,
    ),
    ...notesFor(
      total.verifying,
      (entry) =>
        `${entry.accountName}'s ${monthLabel(entry.month)} statement is still being verified.`,
    ),
    ...notesFor(
      total.midCycle,
      (entry) =>
        `${entry.accountName} has no statement that ends in ${monthLabel(entry.month)}.`,
    ),
    ...total.unknownValue.map((entry) =>
      entry.readWithoutAccount
        ? `The ${monthLabel(entry.month)} statement was read without ${entry.accountName}.`
        : `${entry.accountName}'s ${monthLabel(entry.month)} statement ${entry.failedRead ? 'could not be read' : 'gives no value'}.`,
    ),
    ...notesFor(
      total.twoStatements,
      (entry) =>
        `${entry.accountName} has two statements for ${monthLabel(entry.month)}.`,
    ),
    ...notStartedNotes(total),
  ]
  if (notes.length <= MAX_NOTES) return notes
  const more = notes.length - MAX_NOTES
  return [...notes.slice(0, MAX_NOTES), `And ${countOf(more, 'more account')}.`]
}

// The whole portfolio is one account. One started account out of five is not.
const isSingleAccount = (asOf: AllAccountsAsOf) =>
  asOf.accountCount === 1 && asOf.notStarted.length === 0

// The one account a month covers, never one whose statements start later.
const onlyAccount = (asOf: AllAccountsAsOf) =>
  asOf.accountCount === 1
    ? asOf.accounts.find((account) => !isNotStarted(account.status))
    : undefined

const statementsWithoutCash = (asOf: AllAccountsAsOf) =>
  asOf.accounts.filter(
    (account) => account.status === 'statement' && account.cash == null,
  )

// Cash shows when any statement on the as-of date printed a balance. One with none leaves
// the others' cash in the total, so the labels still say cash.
export const isCashShown = (asOf: AllAccountsAsOf): boolean =>
  asOf.accounts.some(
    (account) => account.status === 'statement' && account.cash != null,
  )

// Without the reconcile feature lukita withholds every cash balance, so the plan is the reason.
export const cashLeftOutNote = (
  asOf: AllAccountsAsOf,
  isCashInPlan: boolean,
): string | null => {
  if (!isCashInPlan) return 'Cash balances are not part of this plan.'
  const names = statementsWithoutCash(asOf).map((account) => account.name)
  if (names.length === 0) return null
  return `Cash missing for ${joinWithAnd(names)}.`
}

export const totalLabel = (asOf: AllAccountsAsOf): string => {
  const date = longDate(asOf.asOfDate)
  const hasCash = isCashShown(asOf)
  if (isSingleAccount(asOf)) {
    return hasCash ? `Securities and cash on ${date}` : `Securities on ${date}`
  }
  const accounts = countedAccountsText(asOf)
  return hasCash
    ? `Total of ${accounts} on ${date}`
    : `Securities in ${accounts} on ${date}`
}

const accountsWithCash = (asOf: AllAccountsAsOf) =>
  asOf.accounts.filter((account) => account.cash != null).length

// "Cash in 2 of 3 accounts" when a statement gives no cash figure, so the label never claims all.
export const partLabel = (
  asOf: AllAccountsAsOf,
  part: 'Securities' | 'Cash',
): string => {
  if (isSingleAccount(asOf)) return part
  const withCash = accountsWithCash(asOf)
  return part === 'Cash' && withCash < asOf.accountCount
    ? `Cash in ${withCash} of ${countOf(asOf.accountCount, 'account')}`
    : `${part} in ${countedAccountsText(asOf)}`
}

// Words beside a figure that say where it came from. One account reads its own figures.
export const securitiesNote = (asOf: AllAccountsAsOf): string | null => {
  const only = onlyAccount(asOf)
  return only ? securitiesSourceNote(only) : null
}

export const cashNote = (asOf: AllAccountsAsOf): string | null => {
  const only = onlyAccount(asOf)
  if (only) return cashSourceNote(only)
  const negative = asOf.accounts.filter((account) => (account.cash ?? 0) < 0)
  if (negative.length === 0) return null
  if (negative.length === 1) {
    return `includes ${numberToCurrency(negative[0].cash ?? 0)} in ${negative[0].name}`
  }
  return `includes margin debits in ${countOf(negative.length, 'account')}`
}

export const changeLabel = (asOf: AllAccountsAsOf): string | null =>
  asOf.change ? `Change since ${longDate(asOf.change.since)}` : null

const groupsByStartMonth = (accounts: StartingAccount[]) => {
  const groups = new Map<string, StartingAccount[]>()
  for (const account of accounts) {
    const key = account.startMonth ?? ''
    groups.set(key, [...(groups.get(key) ?? []), account])
  }
  return Array.from(groups.values())
}

const namesOf = (accounts: StartingAccount[]) =>
  joinWithAnd(accounts.map((account) => account.accountName))

// "includes Fidelity •• 0329, whose statements start Nov 2024", so a step up is not read as growth.
export const startedSinceNote = (asOf: AllAccountsAsOf): string | null => {
  const groups = groupsByStartMonth(asOf.change?.startedSince ?? [])
  if (groups.length === 0) return null
  if (groups.length === 1) {
    return `includes ${namesOf(groups[0])}, whose ${startsText(groups[0][0])}`
  }
  const named = groups.map(
    (group) => `${namesOf(group)} (${startsText(group[0])})`,
  )
  return `includes ${named.join('; ')}`
}

// "3 of 5 accounts" when some accounts' statements start after the month. Null when it covers all.
export const accountsCoveredText = (total: MonthTotal): string | null =>
  total.notStarted.length === 0
    ? null
    : `${total.accountCount} of ${countOf(total.accountCount + total.notStarted.length, 'account')}`

// "3 of 5 accounts" when some accounts sit the month out, so the count never reads as the whole portfolio.
export const countedAccountsText = (total: MonthTotal): string =>
  accountsCoveredText(total) ?? countOf(total.accountCount, 'account')

const accountCountsOf = (series: SeriesPoint[]) =>
  Array.from(new Set(series.map((point) => point.accountCount)))

// A fixed count only when every month covers the same accounts.
export const chartLabel = (series: SeriesPoint[]): string => {
  const counts = accountCountsOf(series)
  if (counts.length > 1) return 'Total of the accounts at each month end'
  return counts[0] === 1
    ? 'Securities and cash at each month end'
    : `Total of the ${countOf(counts[0] ?? 0, 'account')} at each month end`
}
